/**
 * SupplierCombobox
 * ────────────────
 * Free-text input with a datalist of existing suppliers. Lets the SM pick
 * from previously-used suppliers or type a new one. Falls back gracefully
 * when the suppliers endpoint hasn't been hit yet.
 *
 * Native HTML datalist gives us "type to filter" behaviour without a
 * heavyweight Combobox dependency.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so multiple inputs on the same page share one fetch.
let _cachedSuppliers = null;
let _inflight = null;

// Listen for "supplier-list-changed" events from AddSupplierDialog so the
// cache is invalidated the moment a new supplier is registered. Without
// this every SupplierCombobox would keep showing the stale dropdown until
// the user refreshes the page.
if (typeof window !== 'undefined') {
  window.addEventListener('supplier-list-changed', () => {
    _cachedSuppliers = null;
    _inflight = null;
  });
}

async function loadSuppliers() {
  if (_cachedSuppliers) return _cachedSuppliers;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/suppliers`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        _cachedSuppliers = data.suppliers || [];
        return _cachedSuppliers;
      }
    } catch (_e) { /* noop */ }
    _cachedSuppliers = [];
    return _cachedSuppliers;
  })();
  return _inflight;
}

export default function SupplierCombobox({
  value,
  onChange,
  onCommit,
  placeholder = 'supplier (e.g. Repco)',
  className = '',
  testId = 'supplier-input',
  size = 'sm',
}) {
  const listId = useId();
  const [options, setOptions] = useState([]);
  // iter139.4 — Local typing buffer so keystrokes don't ping-pong with
  // an upstream-controlled `value` that gets reset by a parent re-render
  // on every PUT response. Without this the user sees text erased mid-
  // type because: each keystroke → parent setItems → parent persist →
  // PUT response → parent setItems again → child receives a stale
  // `value` prop that overwrites the input.
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef(null);
  const isFocusedRef = useRef(false);

  // Sync local buffer with `value` ONLY when the input isn't currently
  // focused. While the SM is typing we refuse to overwrite their text.
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    let cancelled = false;
    loadSuppliers().then(opts => { if (!cancelled) setOptions(opts || []); });
    const reload = () => {
      loadSuppliers().then(opts => { if (!cancelled) setOptions(opts || []); });
    };
    window.addEventListener('supplier-list-changed', reload);
    return () => {
      cancelled = true;
      window.removeEventListener('supplier-list-changed', reload);
    };
  }, []);

  const heightClass = size === 'xs' ? 'h-7' : size === 'md' ? 'h-11' : 'h-8';

  return (
    <>
      <Input
        ref={inputRef}
        type="text"
        list={listId}
        value={localValue}
        onFocus={() => { isFocusedRef.current = true; }}
        onChange={(e) => {
          const v = e.target.value;
          setLocalValue(v);
          // Still surface every keystroke to legacy onChange callers
          // (e.g. forms that need live preview). Persisting callers
          // should subscribe to onCommit instead.
          onChange?.(v);
        }}
        onBlur={(e) => {
          isFocusedRef.current = false;
          const v = e.target.value;
          // Snap back to the upstream value if it changed during typing.
          // If the caller relies on onCommit (new pattern), `value` may
          // still be the old one — let them update it via onCommit.
          onCommit?.(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.target.blur();  // triggers onBlur → onCommit
          }
        }}
        placeholder={placeholder}
        className={`${heightClass} ${size === 'md' ? 'text-base touch-manipulation' : 'text-xs'} ${className}`}
        data-testid={testId}
      />
      <datalist id={listId}>
        {options.map(o => (
          <option key={o.name} value={o.name}>
            {o.usage_count > 0 ? `used ${o.usage_count}×` : ''}
          </option>
        ))}
      </datalist>
    </>
  );
}
